/**
 * F8 Targeting — gedeelde DTO's, Zod-schemas en label-maps.
 *
 * Concept: acquisitie-/sales-tool waarmee sales-medewerkers prospects (leads)
 * zoeken op afstand vanaf een medewerker-thuisadres en groeperen in
 * acquisitie-lijsten met per-lead status + notitie. Bij conversie wordt een
 * lead gekoppeld aan een bestaande Client (relatie-module).
 *
 * Backoffice-only beheermodule (geen portal-flow). Permission-key TARGETING.
 */
import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { controleerVervolgInhoud, vervolgOpIdVeld } from './contactmoment-draad'
import { partialZonderDefaults } from './zod-patch'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const TARGETING_LEAD_STATUS = [
  'NIEUW',
  'GEBELD',
  'AFSPRAAK',
  'OFFERTE_VERSTUURD',
  'KLANT_GEWORDEN',
  'NIET_INTERESSANT',
] as const
export type TargetingLeadStatus = (typeof TARGETING_LEAD_STATUS)[number]

export const TARGETING_LIST_STATUS = ['ACTIEF', 'AFGEROND', 'GEARCHIVEERD'] as const
export type TargetingListStatus = (typeof TARGETING_LIST_STATUS)[number]

// ─── Labels ─────────────────────────────────────────────────────────────────

export const TARGETING_LEAD_STATUS_LABELS: Record<TargetingLeadStatus, string> = {
  NIEUW: 'Nieuw',
  GEBELD: 'Gebeld',
  AFSPRAAK: 'Afspraak',
  OFFERTE_VERSTUURD: 'Offerte verstuurd',
  KLANT_GEWORDEN: 'Klant geworden',
  NIET_INTERESSANT: 'Niet interessant',
}

export const TARGETING_LIST_STATUS_LABELS: Record<TargetingListStatus, string> = {
  ACTIEF: 'Actief',
  AFGEROND: 'Afgerond',
  GEARCHIVEERD: 'Gearchiveerd',
}

export const TARGETING_LEAD_STATUS_BADGE: Record<
  TargetingLeadStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  NIEUW: 'info',
  GEBELD: 'default',
  AFSPRAAK: 'warning',
  OFFERTE_VERSTUURD: 'warning',
  KLANT_GEWORDEN: 'success',
  NIET_INTERESSANT: 'secondary',
}

export const TARGETING_LIST_STATUS_BADGE: Record<
  TargetingListStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  ACTIEF: 'success',
  AFGEROND: 'default',
  GEARCHIVEERD: 'secondary',
}

// ─── Zod-schemas ────────────────────────────────────────────────────────────

export const TARGETING_LEAD_TYPE = ['LEAD', 'KLANT'] as const
export type TargetingLeadType = (typeof TARGETING_LEAD_TYPE)[number]

export const TARGETING_LEAD_TYPE_LABELS: Record<TargetingLeadType, string> = {
  LEAD: 'Lead',
  KLANT: 'Klant',
}

export const createLeadSchema = z.object({
  bedrijfsnaam: z.string().trim().min(1).max(200),
  branche: z.string().trim().min(1).max(120).nullable().optional(),
  grootte: z.number().int().min(0).max(1_000_000).nullable().optional(),
  website: z.string().trim().max(300).nullable().optional(),
  contactpersoon: z.string().trim().max(200).nullable().optional(),
  telefoon: z.string().trim().max(50).nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  adres: z.string().trim().max(300).nullable().optional(),
  plaats: z.string().trim().max(120).nullable().optional(),
  postcode: z.string().trim().max(20).nullable().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  heeftContract: z.boolean().optional(),
  clientId: z.string().min(1).nullable().optional(),
  score: z.number().int().min(0).max(5).optional(),
  // ── Targeting CRM (BLOK 3) — uitbreiding ────────────────────────────────
  type: z.enum(TARGETING_LEAD_TYPE).optional(),
  klantnummer: z.string().trim().max(50).nullable().optional(),
  kvkNummer: z.string().trim().max(20).nullable().optional(),
  eigenaarId: z.string().min(1).nullable().optional(),
  alarmDatum: z.string().datetime().nullable().optional(),
  // Spec 28 — klant-alarm actief-tot datum (vervangt het oude boolean-alarm).
  klantAlarmTotDatum: z.string().datetime().nullable().optional(),
  labels: z.array(z.string().trim().min(1).max(50)).max(50).optional(),
  straatnaam: z.string().trim().max(120).nullable().optional(),
  huisnummer: z.string().trim().max(20).nullable().optional(),
  huisnummerToevoeging: z.string().trim().max(10).nullable().optional(),
  provincie: z.string().trim().max(60).nullable().optional(),
  landcode: z.string().trim().length(2).optional(),
  offerteEmail: z.string().trim().email().max(200).nullable().optional(),
  invoerdatum: z.string().datetime().optional(),
})
export type CreateLeadInput = z.infer<typeof createLeadSchema>

export const updateLeadSchema = createLeadSchema.partial()
export type UpdateLeadInput = z.infer<typeof updateLeadSchema>

export const listLeadsQuerySchema = z.object({
  zoek: z.string().trim().min(1).optional(),
  branche: z.string().trim().min(1).optional(),
  plaats: z.string().trim().min(1).optional(),
  zonderContract: queryBoolean().optional(),
  // Geocoded medewerker-positie + radius voor afstandsfilter (optioneel).
  vanafEmployeeId: z.string().min(1).optional(),
  straalKm: z.coerce.number().int().min(1).max(500).optional(),
  // Minimale grootte (≥ value).
  minGrootte: z.coerce.number().int().min(0).optional(),
  // Uitgebreide filters (BLOK 4)
  type: z.enum(TARGETING_LEAD_TYPE).optional(),
  eigenaarId: z.string().min(1).optional(),
  geenEigenaar: queryBoolean().optional(),
  minScore: z.coerce.number().int().min(0).max(5).optional(),
  // Spec 28 — klant-alarm filters (vervangen het oude boolean-alarm).
  klantAlarmActief: queryBoolean().optional(),
  klantAlarmStatus: z.enum(['verlopen', 'vandaag', 'aankomend']).optional(),
  metKvk: queryBoolean().optional(),
  metEmail: queryBoolean().optional(),
  metCoords: queryBoolean().optional(),
  // C1 — server-side filter op leads ZONDER coördinaten (kaart-tab "leads
  // zonder adres"-paneel). Voorheen werd dat client-side over de eerste
  // pagina gefilterd, waardoor alles na de eerste pageSize onvindbaar was.
  zonderCoords: queryBoolean().optional(),
  provincie: z.string().trim().min(1).optional(),
  postcode: z.string().trim().min(1).optional(),
  label: z.string().trim().min(1).optional(),
  invoerdatumVan: z.string().datetime().optional(),
  invoerdatumTm: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(50),
})
export type ListLeadsQuery = z.infer<typeof listLeadsQuerySchema>

// Lijst-CRUD
export const createListSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  status: z.enum(TARGETING_LIST_STATUS).optional(),
  toegewezenAanId: z.string().min(1).nullable().optional(),
  notitie: z.string().trim().max(4000).nullable().optional(),
})
export type CreateListInput = z.infer<typeof createListSchema>

export const updateListSchema = createListSchema.partial()
export type UpdateListInput = z.infer<typeof updateListSchema>

export const listListsQuerySchema = z.object({
  zoek: z.string().trim().min(1).optional(),
  status: z.enum(TARGETING_LIST_STATUS).optional(),
  toegewezenAanId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type ListListsQuery = z.infer<typeof listListsQuerySchema>

// Lead aan lijst toevoegen — accepteert array zodat bulk-add werkt.
export const addLeadsToListSchema = z.object({
  leadIds: z.array(z.string().min(1)).min(1).max(500),
  vanafEmployeeId: z.string().min(1).nullable().optional(),
})
export type AddLeadsToListInput = z.infer<typeof addLeadsToListSchema>

// Per-list-item update (status + notitie).
export const updateListItemSchema = z.object({
  status: z.enum(TARGETING_LEAD_STATUS).optional(),
  notitie: z.string().trim().max(4000).nullable().optional(),
})
export type UpdateListItemInput = z.infer<typeof updateListItemSchema>

// Fase 6.5 — Lead-merge: één "loser"-lead wordt soft-deleted en alle relaties
// (list-items, contactpersonen, notities, kansen, taken, contracten) worden
// op de "winner"-lead overgeschreven. clientId / heeftContract van de winner
// blijven leidend; bij ontbreken op winner wordt loser's waarde gemerged.
export const mergeLeadsSchema = z.object({
  winnerId: z.string().min(1),
  loserId: z.string().min(1),
  /** Optioneel: notitie die in de audit-log komt. */
  note: z.string().trim().max(500).optional(),
})
export type MergeLeadsInput = z.infer<typeof mergeLeadsSchema>

export interface MergeLeadsResultDTO {
  winnerId: string
  loserId: string
  moved: {
    listItems: number
    contacts: number
    notes: number
    opportunities: number
    tasks: number
    contracts: number
  }
}

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface TargetingLeadDTO {
  id: string
  bedrijfsnaam: string
  branche: string | null
  grootte: number | null
  website: string | null
  contactpersoon: string | null
  telefoon: string | null
  email: string | null
  adres: string | null
  plaats: string | null
  postcode: string | null
  latitude: number | null
  longitude: number | null
  heeftContract: boolean
  clientId: string | null
  clientName: string | null
  score: number
  // Targeting CRM (BLOK 3) — uitbreiding
  type: TargetingLeadType
  klantnummer: string | null
  kvkNummer: string | null
  eigenaarId: string | null
  eigenaarNaam: string | null
  alarmDatum: string | null
  klantAlarmTotDatum: string | null
  labels: string[]
  straatnaam: string | null
  huisnummer: string | null
  huisnummerToevoeging: string | null
  provincie: string | null
  landcode: string
  offerteEmail: string | null
  invoerdatum: string
  cirqllId: string | null
  createdAt: string
  updatedAt: string
  // Acquisitie-module (ronde 3 — PR-5) — optioneel zodat oudere clients niet
  // breken; nieuwe API-versies vullen deze altijd in.
  linkedinUrl?: string | null
  linkedinContactUrl?: string | null
  acquisitieStatus?: string | null
  acquisitieStartDatum?: string | null
  acquisitieEigenaarId?: string | null
  acquisitieEigenaarNaam?: string | null
  warmteScore?: number | null
  geschatteOmzet?: number | null
  bronKanaal?: string | null
}

export interface TargetingLeadListRowDTO extends TargetingLeadDTO {
  /**
   * Afstand in km vanaf opgegeven medewerker-thuislocatie. Null als geen
   * vanafEmployeeId is opgegeven of geocoding ontbreekt.
   */
  afstandKm: number | null
}

export interface TargetingListEigenaarDTO {
  id: string
  naam: string
}

export interface TargetingListItemDTO {
  id: string
  leadId: string
  status: TargetingLeadStatus
  notitie: string | null
  afstandKm: number | null
  vanafEmployeeId: string | null
  lead: TargetingLeadDTO
  createdAt: string
  updatedAt: string
}

export interface TargetingListRowDTO {
  id: string
  naam: string
  status: TargetingListStatus
  toegewezenAan: TargetingListEigenaarDTO | null
  notitie: string | null
  bedrijvenAantal: number
  createdAt: string
  updatedAt: string
}

export interface TargetingListDetailDTO extends TargetingListRowDTO {
  items: TargetingListItemDTO[]
}

export interface TargetingStatsDTO {
  totaalLeads: number
  leadsZonderContract: number
  leadsKlantGeworden: number
  actieveLijsten: number
  branchesAantal: number
}

// ─── Targeting CRM (BLOK 3) ─────────────────────────────────────────────────
//
// Uitbreiding van Targeting met CRM-functionaliteit (contacten, notities,
// kansen, taken, contracten). Geen breaking changes op bestaande Targeting-
// types — nieuwe schemas/DTO's leven naast de oude.

export const KANS_FASE = [
  'NIEUW',
  'CONTACT_GELEGD',
  'OFFERTE_FASE',
  'ONDERHANDELING',
  'GEWONNEN',
  'VERLOREN',
] as const
export type KansFase = (typeof KANS_FASE)[number]

export const KANS_FASE_LABELS: Record<KansFase, string> = {
  NIEUW: 'Nieuw',
  CONTACT_GELEGD: 'Contact gelegd',
  OFFERTE_FASE: 'Offerte-fase',
  ONDERHANDELING: 'Onderhandeling',
  GEWONNEN: 'Gewonnen',
  VERLOREN: 'Verloren',
}

export const TARGETING_TAAK_STATUS = ['OPEN', 'IN_BEHANDELING', 'VOLTOOID', 'GEANNULEERD'] as const
export type TargetingTaakStatus = (typeof TARGETING_TAAK_STATUS)[number]

export const TARGETING_TAAK_STATUS_LABELS: Record<TargetingTaakStatus, string> = {
  OPEN: 'Open',
  IN_BEHANDELING: 'In behandeling',
  VOLTOOID: 'Voltooid',
  GEANNULEERD: 'Geannuleerd',
}

export const TARGETING_TAAK_PRIORITEIT = ['LAAG', 'NORMAAL', 'HOOG', 'KRITIEK'] as const
export type TargetingTaakPrioriteit = (typeof TARGETING_TAAK_PRIORITEIT)[number]

export const TARGETING_TAAK_PRIORITEIT_LABELS: Record<TargetingTaakPrioriteit, string> = {
  LAAG: 'Laag',
  NORMAAL: 'Normaal',
  HOOG: 'Hoog',
  KRITIEK: 'Kritiek',
}

export const TARGETING_CONTRACT_TYPE = [
  'ONDERHOUD',
  'SLA',
  'PROJECT',
  'RAAMOVEREENKOMST',
  'ABONNEMENT',
  'OVERIG',
] as const
export type TargetingContractType = (typeof TARGETING_CONTRACT_TYPE)[number]

export const TARGETING_CONTRACT_TYPE_LABELS: Record<TargetingContractType, string> = {
  ONDERHOUD: 'Onderhoud',
  SLA: 'SLA',
  PROJECT: 'Project',
  RAAMOVEREENKOMST: 'Raamovereenkomst',
  ABONNEMENT: 'Abonnement',
  OVERIG: 'Overig',
}

export const TARGETING_CONTRACT_STATUS = [
  'CONCEPT',
  'ACTIEF',
  'AFGELOPEN',
  'OPGEZEGD',
] as const
export type TargetingContractStatus = (typeof TARGETING_CONTRACT_STATUS)[number]

export const TARGETING_CONTRACT_STATUS_LABELS: Record<TargetingContractStatus, string> = {
  CONCEPT: 'Concept',
  ACTIEF: 'Actief',
  AFGELOPEN: 'Afgelopen',
  OPGEZEGD: 'Opgezegd',
}

// ─ Contact-schemas ───────────────────────────────────────────────────────────

export const createContactSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  functie: z.string().trim().max(120).nullable().optional(),
  email: z.string().trim().email().max(200).nullable().optional(),
  telefoon: z.string().trim().max(50).nullable().optional(),
  mobiel: z.string().trim().max(50).nullable().optional(),
  isPrimair: z.boolean().optional(),
})
export type CreateContactInput = z.infer<typeof createContactSchema>

export const updateContactSchema = createContactSchema.partial()
export type UpdateContactInput = z.infer<typeof updateContactSchema>

export interface TargetingContactDTO {
  id: string
  leadId: string
  naam: string
  functie: string | null
  email: string | null
  telefoon: string | null
  mobiel: string | null
  isPrimair: boolean
  cirqllImport: boolean
  createdAt: string
  updatedAt: string
}

// ─ Notitie-schemas ───────────────────────────────────────────────────────────

export const createNotitieSchema = z.object({
  tekst: z.string().trim().min(1).max(10_000),
  volgendeContactDatum: z.string().datetime().nullable().optional(),
})
export type CreateNotitieInput = z.infer<typeof createNotitieSchema>

export const updateNotitieSchema = z.object({
  tekst: z.string().trim().min(1).max(10_000).optional(),
  volgendeContactDatum: z.string().datetime().nullable().optional(),
})
export type UpdateNotitieInput = z.infer<typeof updateNotitieSchema>

export interface TargetingNotitieDTO {
  id: string
  leadId: string
  schrijverId: string
  schrijverNaam: string
  tekst: string
  volgendeContactDatum: string | null
  cirqllImport: boolean
  createdAt: string
  updatedAt: string
}

// ─ Kans-schemas ──────────────────────────────────────────────────────────────

export const createKansSchema = z.object({
  eigenaarId: z.string().min(1),
  titel: z.string().trim().min(1).max(200),
  fase: z.enum(KANS_FASE).optional(),
  waarde: z.number().min(0).max(99_999_999),
  kansprocent: z.number().int().min(0).max(100).optional(),
  deadline: z.string().datetime().nullable().optional(),
  verliesReden: z.string().trim().max(2000).nullable().optional(),
})
export type CreateKansInput = z.infer<typeof createKansSchema>

export const updateKansSchema = createKansSchema.partial()
export type UpdateKansInput = z.infer<typeof updateKansSchema>

// Aparte schema voor de drag-drop fase-update — afdwingt verliesReden bij VERLOREN.
export const updateKansFaseSchema = z
  .object({
    fase: z.enum(KANS_FASE),
    verliesReden: z.string().trim().min(1).max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.fase === 'VERLOREN' && (!val.verliesReden || val.verliesReden.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['verliesReden'],
        message: 'Verlies-reden is verplicht bij VERLOREN.',
      })
    }
  })
export type UpdateKansFaseInput = z.infer<typeof updateKansFaseSchema>

export interface TargetingKansDTO {
  id: string
  leadId: string
  leadBedrijfsnaam: string
  eigenaarId: string
  eigenaarNaam: string
  titel: string
  fase: KansFase
  waarde: number
  kansprocent: number
  deadline: string | null
  verliesReden: string | null
  // Batch-7 A2 — configureerbare funnels. Null bij (legacy) kansen die nog
  // niet aan een funnel/stage hangen.
  funnelId: string | null
  stageId: string | null
  createdAt: string
  updatedAt: string
}

export interface KansFaseGroupDTO {
  fase: KansFase
  totaalWaarde: number
  aantal: number
  items: TargetingKansDTO[]
}

export interface TargetingOpportunitiesDTO {
  groups: KansFaseGroupDTO[]
  totaalWaarde: number
  totaalAantal: number
  /**
   * Het kansen-bord is niet periode-/scope-gefilterd, dus de set groeit
   * onbeperkt. De API capt op `cap` rijen; `capped` is true zodra die cap
   * geraakt is zodat de UI een "verfijn met filters"-notitie kan tonen.
   */
  capped?: boolean
  cap?: number
}

// ─ Funnels (batch-7 A2) ──────────────────────────────────────────────────────
//
// Configureerbare funnels bovenop het kansen-kanban. De seed-funnel
// "Standaard acquisitie" mapt 1:1 op de KansFase-enum (via stage.legacyFase);
// de enum blijft bestaan voor backward-compat maar de UI werkt op stages.

export interface FunnelStageDTO {
  id: string
  funnelId: string
  naam: string
  volgorde: number
  kansprocentDefault: number | null
  opvolgtermijnDagenDefault: number | null
  isGewonnen: boolean
  isVerloren: boolean
  legacyFase: KansFase | null
}

export interface FunnelDTO {
  id: string
  naam: string
  omschrijving: string | null
  actief: boolean
  volgorde: number
  isStandaard: boolean
  stages: FunnelStageDTO[]
  /** Aantal niet-verwijderde kansen in deze funnel (voor de beheer-UI). */
  aantalKansen: number
}

export interface KansStageGroupDTO {
  stage: FunnelStageDTO
  totaalWaarde: number
  aantal: number
  items: TargetingKansDTO[]
}

export interface FunnelBoardDTO {
  funnel: { id: string; naam: string }
  groups: KansStageGroupDTO[]
  totaalWaarde: number
  totaalAantal: number
  capped?: boolean
  cap?: number
}

export const createFunnelStageSchema = z.object({
  naam: z.string().trim().min(1).max(100),
  volgorde: z.number().int().min(0).optional(),
  kansprocentDefault: z.number().int().min(0).max(100).nullable().optional(),
  opvolgtermijnDagenDefault: z.number().int().min(1).max(365).nullable().optional(),
  isGewonnen: z.boolean().optional(),
  isVerloren: z.boolean().optional(),
})
export type CreateFunnelStageInput = z.infer<typeof createFunnelStageSchema>

export const updateFunnelStageSchema = createFunnelStageSchema.partial()
export type UpdateFunnelStageInput = z.infer<typeof updateFunnelStageSchema>

export const createFunnelSchema = z.object({
  naam: z.string().trim().min(1).max(100),
  omschrijving: z.string().trim().max(500).nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
  /** Optioneel direct stages meegeven; anders via de stage-endpoints. */
  stages: z.array(createFunnelStageSchema).max(20).optional(),
})
export type CreateFunnelInput = z.infer<typeof createFunnelSchema>

export const updateFunnelSchema = z.object({
  naam: z.string().trim().min(1).max(100).optional(),
  omschrijving: z.string().trim().max(500).nullable().optional(),
  actief: z.boolean().optional(),
  volgorde: z.number().int().min(0).optional(),
})
export type UpdateFunnelInput = z.infer<typeof updateFunnelSchema>

// Stage-verplaatsing op het bord. verliesReden is verplicht wanneer de
// doel-stage isVerloren heeft en de kans nog geen reden had (server-side
// afgedwongen; de UI vraagt er proactief om).
export const updateKansStageSchema = z.object({
  stageId: z.string().min(1),
  verliesReden: z.string().trim().min(1).max(2000).optional(),
})
export type UpdateKansStageInput = z.infer<typeof updateKansStageSchema>

// ─ Check-ins + acquisitie-kalender (batch-7 A3) ──────────────────────────────

export const LEAD_CHECKIN_FREQUENTIE = [
  'WEKEN_2',
  'MAAND',
  'KWARTAAL',
  'HALFJAAR',
  'CUSTOM',
] as const
export type LeadCheckInFrequentie = (typeof LEAD_CHECKIN_FREQUENTIE)[number]

export const LEAD_CHECKIN_FREQUENTIE_LABELS: Record<LeadCheckInFrequentie, string> = {
  WEKEN_2: 'Elke 2 weken',
  MAAND: 'Maandelijks',
  KWARTAAL: 'Per kwartaal',
  HALFJAAR: 'Per half jaar',
  CUSTOM: 'Eigen interval (dagen)',
}

export const LEAD_CHECKIN_ACTIE = ['BELLEN', 'MAILEN', 'BEZOEKEN'] as const
export type LeadCheckInActieType = (typeof LEAD_CHECKIN_ACTIE)[number]

export const LEAD_CHECKIN_ACTIE_LABELS: Record<LeadCheckInActieType, string> = {
  BELLEN: 'Bellen',
  MAILEN: 'Mailen',
  BEZOEKEN: 'Bezoeken',
}

export const createLeadCheckInSchema = z
  .object({
    frequentie: z.enum(LEAD_CHECKIN_FREQUENTIE),
    customDagen: z.number().int().min(1).max(365).nullable().optional(),
    actieType: z.enum(LEAD_CHECKIN_ACTIE).default('BELLEN'),
    verantwoordelijkeId: z.string().min(1),
    volgendeDatum: z.string().datetime(),
    notitie: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.frequentie === 'CUSTOM' && val.customDagen == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['customDagen'],
        message: 'Geef het aantal dagen op bij een eigen interval.',
      })
    }
  })
export type CreateLeadCheckInInput = z.infer<typeof createLeadCheckInSchema>

export const updateLeadCheckInSchema = z.object({
  frequentie: z.enum(LEAD_CHECKIN_FREQUENTIE).optional(),
  customDagen: z.number().int().min(1).max(365).nullable().optional(),
  actieType: z.enum(LEAD_CHECKIN_ACTIE).optional(),
  verantwoordelijkeId: z.string().min(1).optional(),
  volgendeDatum: z.string().datetime().optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
  actief: z.boolean().optional(),
})
export type UpdateLeadCheckInInput = z.infer<typeof updateLeadCheckInSchema>

export interface LeadCheckInDTO {
  id: string
  leadId: string
  leadBedrijfsnaam: string
  frequentie: LeadCheckInFrequentie
  customDagen: number | null
  actieType: LeadCheckInActieType
  verantwoordelijkeId: string
  verantwoordelijkeNaam: string
  volgendeDatum: string
  notitie: string | null
  actief: boolean
  laatsteAfhandelingOp: string | null
  createdAt: string
  updatedAt: string
}

export const ACQUISITIE_KALENDER_ITEM_TYPE = [
  'OPVOLGING',
  'CHECKIN',
  'TAAK',
  'KANS',
] as const
export type AcquisitieKalenderItemType = (typeof ACQUISITIE_KALENDER_ITEM_TYPE)[number]

export const ACQUISITIE_KALENDER_ITEM_LABELS: Record<AcquisitieKalenderItemType, string> = {
  OPVOLGING: 'Opvolging',
  CHECKIN: 'Check-in',
  TAAK: 'Taak',
  KANS: 'Kans-deadline',
}

/** Eén item op de acquisitie-kalender (samengesteld uit bestaande data). */
export interface AcquisitieKalenderItemDTO {
  type: AcquisitieKalenderItemType
  id: string
  leadId: string
  leadBedrijfsnaam: string
  titel: string
  /** ISO-instant; all-day items staan op UTC-middernacht. */
  datum: string
  allDay: boolean
  eigenaarId: string | null
  eigenaarNaam: string | null
  /** Status-tekst voor de chip (bv. OPEN/VERLOPEN bij opvolgingen). */
  status: string | null
}

export const acquisitieKalenderQuerySchema = z.object({
  van: z.string().datetime(),
  tot: z.string().datetime(),
  eigenaarId: z.string().min(1).optional(),
})
export type AcquisitieKalenderQuery = z.infer<typeof acquisitieKalenderQuerySchema>

// ─ Mail-templates + optielijsten + instellingen (batch-7 A5/A6) ─────────────

export const ACQUISITIE_MAIL_PLACEHOLDERS = [
  'bedrijfsnaam',
  'contactpersoon',
  'eigenaar',
] as const

export const createAcquisitieMailTemplateSchema = z.object({
  naam: z.string().trim().min(1).max(120),
  onderwerp: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(10_000),
  actief: z.boolean().optional(),
})
export type CreateAcquisitieMailTemplateInput = z.infer<
  typeof createAcquisitieMailTemplateSchema
>

export const updateAcquisitieMailTemplateSchema =
  createAcquisitieMailTemplateSchema.partial()
export type UpdateAcquisitieMailTemplateInput = z.infer<
  typeof updateAcquisitieMailTemplateSchema
>

export interface AcquisitieMailTemplateDTO {
  id: string
  naam: string
  onderwerp: string
  body: string
  actief: boolean
  createdAt: string
  updatedAt: string
}

// Versturen per lead: onderwerp/body komen (eventueel gepersonaliseerd) uit
// de UI; aanEmail default = lead.email.
export const verstuurLeadMailSchema = z.object({
  templateId: z.string().min(1).optional(),
  onderwerp: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(10_000),
  aanEmail: z.string().trim().email().max(200).optional(),
})
export type VerstuurLeadMailInput = z.infer<typeof verstuurLeadMailSchema>

export const ACQUISITIE_OPTIELIJST_CATEGORIE = [
  'BRANCHE',
  'LEADBRON',
  'BEHOEFTETYPE',
] as const
export type AcquisitieOptielijstCategorie =
  (typeof ACQUISITIE_OPTIELIJST_CATEGORIE)[number]

export const ACQUISITIE_OPTIELIJST_CATEGORIE_LABELS: Record<
  AcquisitieOptielijstCategorie,
  string
> = {
  BRANCHE: 'Branches / bedrijfstypes',
  LEADBRON: 'Leadbronnen',
  BEHOEFTETYPE: 'Behoeftetypes',
}

export interface AcquisitieOptieDTO {
  id: string
  categorie: AcquisitieOptielijstCategorie
  waarde: string
  volgorde: number
  actief: boolean
}

// ─ Taak-schemas ──────────────────────────────────────────────────────────────

export const createTaakSchema = z.object({
  eigenaarId: z.string().min(1),
  titel: z.string().trim().min(1).max(200),
  omschrijving: z.string().trim().max(4000).nullable().optional(),
  status: z.enum(TARGETING_TAAK_STATUS).optional(),
  prioriteit: z.enum(TARGETING_TAAK_PRIORITEIT).optional(),
  deadline: z.string().datetime().nullable().optional(),
})
export type CreateTaakInput = z.infer<typeof createTaakSchema>

export const updateTaakSchema = createTaakSchema.partial()
export type UpdateTaakInput = z.infer<typeof updateTaakSchema>

export interface TargetingTaakDTO {
  id: string
  leadId: string
  leadBedrijfsnaam: string
  eigenaarId: string
  eigenaarNaam: string
  titel: string
  omschrijving: string | null
  status: TargetingTaakStatus
  prioriteit: TargetingTaakPrioriteit
  deadline: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

// ─ Contract-schemas ──────────────────────────────────────────────────────────

export const createContractSchema = z.object({
  type: z.enum(TARGETING_CONTRACT_TYPE),
  status: z.enum(TARGETING_CONTRACT_STATUS).optional(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime().nullable().optional(),
  waarde: z.number().min(0).max(99_999_999).nullable().optional(),
  omschrijving: z.string().trim().max(4000).nullable().optional(),
})
export type CreateContractInput = z.infer<typeof createContractSchema>

export const updateContractSchema = createContractSchema.partial()
export type UpdateContractInput = z.infer<typeof updateContractSchema>

export interface TargetingContractDTO {
  id: string
  leadId: string
  type: TargetingContractType
  status: TargetingContractStatus
  startDate: string
  endDate: string | null
  waarde: number | null
  omschrijving: string | null
  createdAt: string
  updatedAt: string
}

// ─ Lead-detail (uitgebreid met include) ──────────────────────────────────────

export interface TargetingLeadDetailDTO extends TargetingLeadDTO {
  contactpersonen: TargetingContactDTO[]
  notities: TargetingNotitieDTO[]
  kansen: TargetingKansDTO[]
  taken: TargetingTaakDTO[]
  contracten: TargetingContractDTO[]
  // Lijsten waar deze lead in zit (alleen meta-info, niet de items zelf).
  lijsten: Array<{
    id: string
    naam: string
    listItemId: string
    status: TargetingLeadStatus
  }>
}

// ─ Dashboard-stats v2 (uitgebreid t.o.v. originele TargetingStatsDTO) ────────

export interface TargetingDashboardStatsDTO extends TargetingStatsDTO {
  klantAlarmen: number
  openKansen: number
  openTaken: number
  faseTrechter: Array<{ fase: KansFase; aantal: number; totaalWaarde: number }>
}

// ─ Import-payload ────────────────────────────────────────────────────────────

export const importCirqllSchema = z.object({
  // Path naar XLSX/CSV op de server (alleen ADMIN).
  filename: z.string().trim().min(1),
  // Dry-run: alleen rapportage, geen mutaties.
  dryRun: z.boolean().optional(),
})
export type ImportCirqllInput = z.infer<typeof importCirqllSchema>

export interface TargetingImportResultDTO {
  totaalRijen: number
  leadsAangemaakt: number
  leadsBijgewerkt: number
  contactenAangemaakt: number
  notitiesAangemaakt: number
  fouten: Array<{ rij: number; bericht: string }>
}

// ═══════════════════════════════════════════════════════════════════════════
// Acquisitie-module (ronde 3 — PR-5)
// ═══════════════════════════════════════════════════════════════════════════

// ── Enums & label-maps ──────────────────────────────────────────────────────

export const ACQUISITIE_STATUS = [
  'PROSPECT',
  'EERSTE_CONTACT',
  'IN_GESPREK',
  'AFSPRAAK_GEPLAND',
  'OFFERTE_VERSTUURD',
  'ONDERHANDELING',
  'GEWONNEN',
  'VERLOREN',
  'ON_HOLD',
] as const
export type AcquisitieStatus = (typeof ACQUISITIE_STATUS)[number]

export const ACQUISITIE_STATUS_LABELS: Record<AcquisitieStatus, string> = {
  PROSPECT: 'Prospect',
  EERSTE_CONTACT: 'Eerste contact',
  IN_GESPREK: 'In gesprek',
  AFSPRAAK_GEPLAND: 'Afspraak gepland',
  OFFERTE_VERSTUURD: 'Offerte verstuurd',
  ONDERHANDELING: 'Onderhandeling',
  GEWONNEN: 'Gewonnen',
  VERLOREN: 'Verloren',
  ON_HOLD: 'On hold',
}

export const ACQUISITIE_STATUS_BADGE: Record<
  AcquisitieStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  PROSPECT: 'default',
  EERSTE_CONTACT: 'info',
  IN_GESPREK: 'info',
  AFSPRAAK_GEPLAND: 'warning',
  OFFERTE_VERSTUURD: 'warning',
  ONDERHANDELING: 'secondary',
  GEWONNEN: 'success',
  VERLOREN: 'destructive',
  ON_HOLD: 'default',
}

/** Links→rechts volgorde voor pipeline-trechter. GEWONNEN is laatste node;
 *  VERLOREN/ON_HOLD blijven daarbuiten als "afhakers". */
export const ACQUISITIE_PIPELINE_ORDER: AcquisitieStatus[] = [
  'PROSPECT',
  'EERSTE_CONTACT',
  'IN_GESPREK',
  'AFSPRAAK_GEPLAND',
  'OFFERTE_VERSTUURD',
  'ONDERHANDELING',
  'GEWONNEN',
]

export const ACQUISITIE_CONTACT_TYPE = [
  'LINKEDIN_CONNECTIE',
  'LINKEDIN_BERICHT',
  'LINKEDIN_REACTIE',
  'EMAIL',
  'TELEFOON',
  'VIDEOCALL',
  'BEZOEK',
  'BEURS',
  'WHATSAPP',
  'OFFERTE',
  'OVERIG',
] as const
export type AcquisitieContactType = (typeof ACQUISITIE_CONTACT_TYPE)[number]

export const ACQUISITIE_CONTACT_TYPE_LABELS: Record<AcquisitieContactType, string> = {
  LINKEDIN_CONNECTIE: 'LinkedIn connectie',
  LINKEDIN_BERICHT: 'LinkedIn bericht',
  LINKEDIN_REACTIE: 'LinkedIn reactie',
  EMAIL: 'E-mail',
  TELEFOON: 'Telefoon',
  VIDEOCALL: 'Videocall',
  BEZOEK: 'Bezoek',
  BEURS: 'Beurs/event',
  WHATSAPP: 'WhatsApp',
  OFFERTE: 'Offerte',
  OVERIG: 'Overig',
}

/** lucide-react icon-naam per contact-type (icoon-mapping voor UI). */
export const ACQUISITIE_CONTACT_TYPE_ICONS: Record<AcquisitieContactType, string> = {
  LINKEDIN_CONNECTIE: 'Linkedin',
  LINKEDIN_BERICHT: 'Linkedin',
  LINKEDIN_REACTIE: 'Linkedin',
  EMAIL: 'Mail',
  TELEFOON: 'Phone',
  VIDEOCALL: 'Video',
  BEZOEK: 'MapPin',
  BEURS: 'Users',
  WHATSAPP: 'MessageCircle',
  OFFERTE: 'FileText',
  OVERIG: 'MoreHorizontal',
}

export const BRON_KANAAL = [
  'LINKEDIN',
  'COLD_CALL',
  'REFERRAL',
  'WEBSITE',
  'BEURS',
  'NETWERK',
  'BESTAANDE_KLANT',
  'OVERIG',
] as const
export type BronKanaal = (typeof BRON_KANAAL)[number]

export const BRON_KANAAL_LABELS: Record<BronKanaal, string> = {
  LINKEDIN: 'LinkedIn',
  COLD_CALL: 'Cold call',
  REFERRAL: 'Doorverwijzing',
  WEBSITE: 'Website',
  BEURS: 'Beurs/event',
  NETWERK: 'Netwerk',
  BESTAANDE_KLANT: 'Bestaande klant',
  OVERIG: 'Overig',
}

export const OPVOLG_PRIORITEIT = ['LAAG', 'NORMAAL', 'HOOG', 'URGENT'] as const
export type OpvolgPrioriteit = (typeof OPVOLG_PRIORITEIT)[number]

export const OPVOLG_PRIORITEIT_LABELS: Record<OpvolgPrioriteit, string> = {
  LAAG: 'Laag',
  NORMAAL: 'Normaal',
  HOOG: 'Hoog',
  URGENT: 'Urgent',
}

export const OPVOLG_STATUS = ['OPEN', 'VERLOPEN', 'VOLTOOID', 'OVERGESLAGEN'] as const
export type OpvolgStatus = (typeof OPVOLG_STATUS)[number]

export const OPVOLG_STATUS_LABELS: Record<OpvolgStatus, string> = {
  OPEN: 'Open',
  VERLOPEN: 'Verlopen',
  VOLTOOID: 'Voltooid',
  OVERGESLAGEN: 'Overgeslagen',
}

// ── Status-transitie suggesties (spec §5) ───────────────────────────────────

/** Automatische status-overgang per contact-type. GEWONNEN/VERLOREN/ON_HOLD
 *  worden altijd handmatig gezet — niet via deze map. */
export const ACQUISITIE_AUTO_TRANSITIONS: Partial<
  Record<AcquisitieContactType, AcquisitieStatus>
> = {
  LINKEDIN_CONNECTIE: 'EERSTE_CONTACT',
  LINKEDIN_BERICHT: 'EERSTE_CONTACT',
  EMAIL: 'EERSTE_CONTACT',
  TELEFOON: 'IN_GESPREK',
  VIDEOCALL: 'IN_GESPREK',
  BEZOEK: 'AFSPRAAK_GEPLAND',
  OFFERTE: 'OFFERTE_VERSTUURD',
}

/** Suggesteert een vooruit-transitie op basis van huidige status + contact-type.
 *  Returnt null als de gewenste status niet verder ligt dan de huidige (geen
 *  achteruit-transities) of als de target-status GEWONNEN/VERLOREN/ON_HOLD is. */
export function suggestAcquisitieStatusTransition(
  current: AcquisitieStatus,
  contactType: AcquisitieContactType,
): AcquisitieStatus | null {
  const suggested = ACQUISITIE_AUTO_TRANSITIONS[contactType]
  if (!suggested) return null
  const currentIdx = ACQUISITIE_PIPELINE_ORDER.indexOf(current)
  const suggestedIdx = ACQUISITIE_PIPELINE_ORDER.indexOf(suggested)
  const gewonnenIdx = ACQUISITIE_PIPELINE_ORDER.indexOf('GEWONNEN')
  // Buiten pipeline (current=VERLOREN/ON_HOLD/GEWONNEN) → niet auto-promoten.
  if (currentIdx < 0) return null
  // Alleen vooruit + niet voorbij GEWONNEN.
  if (suggestedIdx > currentIdx && suggestedIdx < gewonnenIdx) return suggested
  return null
}

// ── Zod schemas ─────────────────────────────────────────────────────────────

/**
 * Sta lege string of null toe (formulier-input) maar valideer wel als het
 * ingevuld is. De backoffice-edit-forms sturen een leeg veld als expliciete
 * `null` (`x.trim() === '' ? null : x`), dus `z.null()` moet in de union staan,
 * anders weigert Zod de `null` en krijgt de gebruiker een spurious 400
 * "Ongeldige invoer". Output is genormaliseerd naar `string | null`.
 */
const optionalUrlOrEmpty = z
  .union([z.literal(''), z.string().url(), z.null()])
  .optional()
  .transform((v) => (v === '' || v == null ? null : v))

const acquisitieContactmomentVelden = z.object({
  datum: z.coerce.date(),
  type: z.enum(ACQUISITIE_CONTACT_TYPE),
  onderwerp: z.string().trim().min(1).max(200),
  // Notitie heeft een ruime upper-bound (10 000 chars) voor uitgebreide
  // verslagen. Bij auto-opvolging wordt de notitie gesliced naar 2000
  // chars (zie route /acquisitie/.../contactmomenten) zodat de opvolging
  // niet de hele aantekening meekrijgt.
  notitie: z.string().trim().min(1).max(10000),
  resultaat: z.string().trim().max(200).nullable().optional(),
  contactpersoonId: z.string().min(1).nullable().optional(),
  medewerkerId: z.string().min(1).nullable().optional(),
  linkedinMessageUrl: optionalUrlOrEmpty,
  opvolgingNodig: z.boolean().default(false),
  opvolgingDatum: z.coerce.date().nullable().optional(),
  opvolgingType: z.enum(ACQUISITIE_CONTACT_TYPE).nullable().optional(),
  opvolgingPrioriteit: z.enum(OPVOLG_PRIORITEIT).default('NORMAAL'),
  // batch-49 acquisitie-threads (FO-399) — draadkoppeling. De route resolveert
  // dit naar de WORTEL van de draad (vervolg-op-vervolg landt in dezelfde
  // draad) en toetst dat de datum niet vóór het bovenliggende moment ligt.
  vervolgOpId: vervolgOpIdVeld,
})

export const createAcquisitieContactmomentSchema = acquisitieContactmomentVelden.superRefine(
  (waarde, ctx) => controleerVervolgInhoud(waarde, 'notitie', ctx),
)
export type CreateAcquisitieContactmomentInput = z.infer<
  typeof createAcquisitieContactmomentSchema
>

// Bewerken koppelt nooit achteraf aan een draad (spec §5.5): `vervolgOpId`
// staat bewust niet in het update-schema.
export const updateAcquisitieContactmomentSchema = partialZonderDefaults(
  acquisitieContactmomentVelden.omit({ vervolgOpId: true }),
)
export type UpdateAcquisitieContactmomentInput = z.infer<
  typeof updateAcquisitieContactmomentSchema
>

export const createAcquisitieOpvolgingSchema = z.object({
  geplandeDatum: z.coerce.date(),
  type: z.enum(ACQUISITIE_CONTACT_TYPE),
  prioriteit: z.enum(OPVOLG_PRIORITEIT).default('NORMAAL'),
  onderwerp: z.string().trim().min(1).max(200),
  notitie: z.string().trim().max(2000).nullable().optional(),
  toegewezenAanId: z.string().min(1).nullable().optional(),
})
export type CreateAcquisitieOpvolgingInput = z.infer<
  typeof createAcquisitieOpvolgingSchema
>

export const updateAcquisitieOpvolgingSchema = z.object({
  geplandeDatum: z.coerce.date().optional(),
  type: z.enum(ACQUISITIE_CONTACT_TYPE).optional(),
  prioriteit: z.enum(OPVOLG_PRIORITEIT).optional(),
  onderwerp: z.string().trim().min(1).max(200).optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
  toegewezenAanId: z.string().min(1).nullable().optional(),
})
export type UpdateAcquisitieOpvolgingInput = z.infer<
  typeof updateAcquisitieOpvolgingSchema
>

export const voltooiOpvolgingSchema = z.object({
  notitie: z.string().trim().max(2000).optional(),
  resultaatContactmomentId: z.string().min(1).optional(),
})
export type VoltooiOpvolgingInput = z.infer<typeof voltooiOpvolgingSchema>

export const overslaanOpvolgingSchema = z.object({
  reden: z.string().trim().min(1).max(500),
})
export type OverslaanOpvolgingInput = z.infer<typeof overslaanOpvolgingSchema>

export const listOpvolgingenQuerySchema = z.object({
  status: z.enum(OPVOLG_STATUS).optional(),
  prioriteit: z.enum(OPVOLG_PRIORITEIT).optional(),
  toegewezenAanId: z.string().optional(),
  van: z.coerce.date().optional(),
  tot: z.coerce.date().optional(),
  leadId: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(500).default(50),
})

export const listContactmomentenQuerySchema = z.object({
  type: z.enum(ACQUISITIE_CONTACT_TYPE).optional(),
  van: z.coerce.date().optional(),
  tot: z.coerce.date().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(500).default(50),
})

export const updateLeadAcquisitieSchema = z.object({
  linkedinUrl: optionalUrlOrEmpty,
  linkedinContactUrl: optionalUrlOrEmpty,
  acquisitieStatus: z.enum(ACQUISITIE_STATUS).optional(),
  acquisitieStartDatum: z.coerce.date().nullable().optional(),
  acquisitieEigenaarId: z.string().min(1).nullable().optional(),
  warmteScore: z.number().int().min(1).max(10).nullable().optional(),
  geschatteOmzet: z.number().positive().nullable().optional(),
  bronKanaal: z.enum(BRON_KANAAL).nullable().optional(),
})
export type UpdateLeadAcquisitieInput = z.infer<typeof updateLeadAcquisitieSchema>

// ── DTOs ────────────────────────────────────────────────────────────────────

export interface AcquisitieContactmomentDTO {
  id: string
  leadId: string
  datum: string
  type: AcquisitieContactType
  onderwerp: string
  notitie: string
  resultaat: string | null
  medewerkerId: string | null
  medewerkerNaam: string | null
  contactpersoonId: string | null
  contactpersoonNaam: string | null
  linkedinMessageUrl: string | null
  opvolgingNodig: boolean
  opvolgingDatum: string | null
  opvolgingType: AcquisitieContactType | null
  opvolgingId: string | null
  opvolgingStatus: OpvolgStatus | null
  /**
   * batch-49 acquisitie-threads (FO-399) — id van de WORTEL van de draad, of
   * `null` als dit moment zelf de wortel is. Zie `bouwContactmomentDraden`
   * (`@streep/utils`) voor het groeperen.
   */
  vervolgOpId: string | null
  createdAt: string
  updatedAt: string
}

/** Read-through acquisitie-historie van een relatie (alle gekoppelde leads). */
export interface ClientAcquisitieHistorieDTO {
  notities: TargetingNotitieDTO[]
  contactmomenten: AcquisitieContactmomentDTO[]
}

export interface AcquisitieOpvolgingDTO {
  id: string
  leadId: string
  leadBedrijfsnaam: string | null
  geplandeDatum: string
  type: AcquisitieContactType
  prioriteit: OpvolgPrioriteit
  onderwerp: string
  notitie: string | null
  status: OpvolgStatus
  uitgevoerdOp: string | null
  uitgevoerdDoorId: string | null
  uitgevoerdDoorNaam: string | null
  bronContactmomentId: string | null
  // Batch-7 A3 — gezet wanneer deze opvolging door de check-in-cron is aangemaakt.
  bronCheckInId: string | null
  resultaatContactmomentId: string | null
  isAiSuggestie: boolean
  aiRedenering: string | null
  redenOverslaan: string | null
  toegewezenAanId: string | null
  toegewezenAanNaam: string | null
  acquisitieStatus: AcquisitieStatus | null
  createdAt: string
  updatedAt: string
}

export interface AcquisitiePipelineGroupDTO {
  status: AcquisitieStatus
  count: number
  totaalGeschatteOmzet: number
  gemiddeldeWarmte: number | null
  leads: Array<{
    id: string
    bedrijfsnaam: string
    warmteScore: number | null
    geschatteOmzet: number | null
    dagenSindsLaatsteContact: number | null
  }>
}

export interface AcquisitiePipelineDTO {
  pipeline: AcquisitiePipelineGroupDTO[]
  totaalLeads: number
  conversie: {
    prospectNaarGesprek: number
    gesprekNaarOfferte: number
    offerteNaarKlant: number
    gemiddeldeDoorlooptijdDagen: number | null
  }
  openOpvolgingen: number
  verlopenOpvolgingen: number
  contactenDezeWeek: number
  pipelineWaarde: number
}

export interface AcquisitieDashboardStatsDTO {
  actieveProspects: number
  openOpvolgingen: number
  verlopenOpvolgingen: number
  contactenDezeWeek: number
  contactenVorigeWeek: number
  gemiddeldeWarmteScore: number | null
  topBronKanalen: Array<{ kanaal: BronKanaal; count: number }>
  pipelineWaarde: number
  aiAnalyseBeschikbaar: boolean
}

export interface AcquisitieAiPrioriteitDTO {
  prioriteit: OpvolgPrioriteit
  leadId: string
  bedrijfsnaam: string
  situatie: string
  advies: string
  suggestieType: AcquisitieContactType
  suggestieDatum: string
}

export interface AcquisitieAiSamenvattingDTO {
  totaalActief: number
  verlopenOpvolgingen: number
  pipelineWaarde: number
  aandachtspunten: string[]
  positiefNieuws: string[]
}

export interface AcquisitieAiAnalyseDTO {
  id: string
  jaar: number
  weekNummer: number
  weekAdvies: string
  samenvatting: AcquisitieAiSamenvattingDTO
  knelpunten: string[]
  prioriteiten: AcquisitieAiPrioriteitDTO[]
  aantalSuggesties: number
  modelGebruikt: string
  tokensUsed: number | null
  duurMs: number | null
  createdAt: string
  /** Laatste keer dat de analyse is gegenereerd. De rij is een weekly upsert op
   *  (jaar, weekNummer), dus een her-run binnen dezelfde week verschuift
   *  `updatedAt` wel maar `createdAt` niet. UI toont dit als "Gegenereerd op". */
  updatedAt: string
}

// ═══════════════════════════════════════════════════════════════════════════
// batch-53 acquisitie-vervolg (FO-407) - kopje "Vervolgcontacten"
// ═══════════════════════════════════════════════════════════════════════════
//
// GEEN nieuw datamodel (FO §11.1): het overzicht is een query op de bestaande
// `TargetingLead.alarmDatum` + per lead het nieuwste van notitie/contactmoment.
// Deze contracten zijn dus puur lees-DTO's plus twee kleine mutatie-schemas.

/** Waar het "laatste contact" vandaan komt (FO §6.5 - bron-badge in de rij). */
export const VERVOLG_CONTACT_BRON = ['NOTITIE', 'CONTACTMOMENT'] as const
export type VervolgContactBron = (typeof VERVOLG_CONTACT_BRON)[number]

export const VERVOLG_CONTACT_BRON_LABELS: Record<VervolgContactBron, string> = {
  NOTITIE: 'notitie',
  CONTACTMOMENT: 'contactmoment',
}

/**
 * Het laatste contact met een lead, volledig uitgetypt (FO §B.4). Wordt in ÉÉN
 * samengestelde server-query voor alle rijen van de pagina opgehaald - nooit
 * per rij (geen N+1).
 */
export interface VervolgcontactLaatsteContactDTO {
  bron: VervolgContactBron
  /** Volledige tekst; de UI toont de eerste regels en klapt de rest uit. */
  tekst: string
  /** Onderwerp/type-regel bij een contactmoment; null bij een notitie. */
  onderwerp: string | null
  /** ISO-datum van het contact (notitie: createdAt, contactmoment: datum). */
  datum: string
  schrijverNaam: string | null
}

/** Urgentie-band van een vervolgdatum, afgeleid in NL-tijd op de server. */
export const VERVOLG_BAND = ['VERLOPEN', 'VANDAAG', 'LATER'] as const
export type VervolgBand = (typeof VERVOLG_BAND)[number]

export interface VervolgcontactRowDTO {
  leadId: string
  bedrijfsnaam: string
  contactpersoon: string | null
  telefoon: string | null
  /** NL-kalenderdag van `lead.alarmDatum`, als ISO-datetime. */
  vervolgDatum: string
  band: VervolgBand
  acquisitieStatus: AcquisitieStatus
  eigenaarId: string | null
  eigenaarNaam: string | null
  /** Terugval-eigenaar (FO §6.1) wanneer `eigenaarId` leeg is. */
  acquisitieEigenaarId: string | null
  acquisitieEigenaarNaam: string | null
  /** FO §6.3 - geconverteerd naar relatie (FO-400) houdt zijn alarm. */
  geconverteerd: boolean
  clientId: string | null
  /** FO §6.3 - VERLOREN-lead krijgt een badge zodat opruimen makkelijk is. */
  verloren: boolean
  laatsteContact: VervolgcontactLaatsteContactDTO | null
}

/**
 * Periode-filter.
 *
 * Default is `alles` - het kopje heet "alle geplande vervolgcontacten" (§B.3 +
 * acceptatiecriterium 2), dus een vervolgmoment van volgende week hoort er
 * gewoon in te staan. De chronologische sortering zet verlopen en vandaag
 * vanzelf bovenaan, dus je verliest niets door ook vooruit te kijken; de
 * badge-teller blijft onveranderd "vandaag + verlopen".
 *
 * `open` blijft als bewuste versmalling ("alleen wat nu moet").
 */
export const VERVOLG_PERIODE = ['alles', 'open', 'verlopen', 'vandaag', 'week', 'maand'] as const
export type VervolgPeriode = (typeof VERVOLG_PERIODE)[number]

export const VERVOLG_PERIODE_LABELS: Record<VervolgPeriode, string> = {
  alles: 'Alles',
  open: 'Verlopen + vandaag',
  verlopen: 'Alleen verlopen',
  vandaag: 'Alleen vandaag',
  // Bewust "t/m": de grens is "alles tot en met die dag", inclusief wat al
  // verlopen is - niet de kalenderweek/-maand.
  week: 'T/m volgende week',
  maand: 'T/m volgende maand',
}

/**
 * `eigenaarId` = een specifieke eigenaar; `ALLE` = alle eigenaren (FO §6.1:
 * leads zonder eigenaar zijn daar altijd zichtbaar); `GEEN` = uitsluitend de
 * leads zonder enige eigenaar. Default in de UI is de ingelogde gebruiker.
 */
export const listVervolgcontactenQuerySchema = z.object({
  eigenaarId: z.string().min(1).optional(),
  alleEigenaren: queryBoolean().optional(),
  geenEigenaar: queryBoolean().optional(),
  acquisitieStatus: z.enum(ACQUISITIE_STATUS).optional(),
  periode: z.enum(VERVOLG_PERIODE).default('alles'),
  zoek: z.string().trim().min(1).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
})
export type ListVervolgcontactenQuery = z.infer<typeof listVervolgcontactenQuerySchema>

/**
 * Meta bij de lijst. `vandaagAantal`/`verlopenAantal` zijn SERVER-SIDE tellingen
 * over de volledige `where` (nooit over de geladen pagina) - dat is exact de
 * badge-teller op het kopje.
 */
export interface VervolgcontactenMetaDTO {
  total: number
  page: number
  pageSize: number
  vandaagAantal: number
  verlopenAantal: number
  /** `vandaagAantal + verlopenAantal` - de badge op de tab. */
  badge: number
}

/**
 * FO §B.5 - "Afgehandeld" (alarm wissen) en "Doorschuiven" (alleen de datum
 * verzetten). Eén PATCH met een expliciete actie zodat het audit-spoor de
 * bedoeling vastlegt en niet alleen "alarmDatum gewijzigd".
 */
export const VERVOLG_ACTIE = ['AFGEHANDELD', 'DOORGESCHOVEN'] as const
export type VervolgActie = (typeof VERVOLG_ACTIE)[number]

export const vervolgcontactActieSchema = z
  .object({
    actie: z.enum(VERVOLG_ACTIE),
    /** Verplicht bij DOORGESCHOVEN, genegeerd bij AFGEHANDELD. */
    nieuweDatum: z.string().datetime().nullable().optional(),
    notitie: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => v.actie !== 'DOORGESCHOVEN' || Boolean(v.nieuweDatum), {
    message: 'Kies een nieuwe vervolgdatum',
    path: ['nieuweDatum'],
  })
export type VervolgcontactActieInput = z.infer<typeof vervolgcontactActieSchema>

/** Audit-acties op de lead (targeting_audits.action is een vrije String). */
export const VERVOLG_AUDIT_ACTIONS = {
  AFGEHANDELD: 'VERVOLGCONTACT_AFGEHANDELD',
  DOORGESCHOVEN: 'VERVOLGCONTACT_DOORGESCHOVEN',
} as const satisfies Record<VervolgActie, string>
